// Configurator state: single source of truth for the Tesla-style page.
// `selectedMachineId` is one ID — it covers Statler, Ascend, Vision, AND
// the upgrade products (Ascend Retrofit / Ascend Upgrade); they're all
// mutually exclusive "what are you buying as the main item" choices,
// surfaced as three tabs in the Machine section.

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from "react";
import type { Dispatch, ReactNode } from "react";

import { api } from "./api";
import type {
  ActivePromo,
  DealMeta,
  DiscountType,
  GenerateResponse,
  PreviewResponse,
  Product,
} from "./api";

/** Banner state derived from the active promos + the current cart. */
export interface PromoBanner {
  status: "applied" | "available" | null;
  promo: ActivePromo | null;
  /** Estimated savings for the "applied" state (whole cents). */
  savingsCents: number;
}
import { buildCatalog, familyLabel } from "./catalog";
import { displayNameFor, isLower48Zip } from "./format";
import { signalPriceCalculated, signalQuoteRequested } from "./tracking";
import type {
  AccessoryProduct,
  Catalog,
  CategorizedProduct,
  MachineFamily,
} from "./catalog";
import {
  applyBlocks,
  defaultAccessoriesFor,
  effectivePriceCents,
  ID,
  isAccessoryVisible,
  retrofitTradeInCredits,
  tableOptionsForThroatSize,
  type TradeInCredit,
} from "./rules";

export interface CustomerInfo {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zip: string;
}

interface State {
  catalog: Catalog | null;
  catalogLoading: boolean;
  catalogError: string | null;

  selectedMachineId: string | null;
  selectedAccessoryIds: string[];
  /** Table is its own slot — auto-set by machine selection, swappable
   *  via the Tables section. Null when an upgrade is selected (no
   *  table on retrofits). */
  selectedTableId: string | null;
  /** Per-upgrade "tell us about your existing system" answers, keyed by
   *  field id. Reset when the customer leaves the upgrade context. */
  existingSystemAnswers: Record<string, string>;

  customer: CustomerInfo;

  preview: PreviewResponse | null;
  previewLoading: boolean;
  previewError: string | null;

  /** True once the customer has clicked Calculate. Gates the Summary
   *  section's totals display + flips the primary CTA from "Calculate"
   *  to "Email me the quote". */
  calculated: boolean;
  /** True if our lead-capture call has succeeded for the current contact.
   *  Set after Calculate. */
  leadCaptured: boolean;
  capturedContactId: string | null;

  generated: GenerateResponse | null;
  submitting: boolean;
  submitError: string | null;

  /** Active auto-apply promos (fetched once). Drive the promo banner/nudge. */
  activePromos: ActivePromo[];

  /** Employee mode (Gammill-rep superpowers). Off for customers. */
  employee: EmployeeState;

  /** Financing assumptions the monthly-payment math runs on. Editable
   *  via the inline "Edit Financing" panel under the Summary section. The
   *  machine shows cash + monthly together; accessories/tables show cash. */
  financing: FinancingParams;

  /** Set when launched as a pre-loved deep-link (gammill.com/preloved "Build
   *  Machine"). Fixes the main item to one used machine (name + price
   *  overridden) and routes the quote to the Pre-Loved template. Null = the
   *  normal build-your-own flow. */
  preloved: {
    productId: string;
    family: MachineFamily;
    description: string;
    priceCents: number;
    /** Optional photo of the actual used machine, passed via the deep-link
     *  `preloved_image` param. Empty when the sending system has no per-unit
     *  photo yet — Hero then falls back to the generic family image. */
    imageUrl: string;
    /** Deal-name category for this used unit: "Pre-Loved" | "Paint Flaw" |
     *  "Show" | "Classroom" (from `preloved_category`, default Pre-Loved). */
    category: string;
    /** 4-digit year when dated; null when graded by class (mutually exclusive
     *  with klass). Year ranges are never accepted here. */
    year: string | null;
    /** Grade "A" | "B" | "C" when graded; null when dated. */
    klass: string | null;
    /** Throat size in inches from `preloved_throat`, when known. */
    throat: number | null;
  } | null;
}

/** Employee mode: the superpowers a Gammill rep applies. `active` flips on
 *  after a successful PIN unlock; `token` is the signed session token the
 *  generate call presents. Held in memory only (never persisted) so an
 *  Incognito session re-auths from scratch — intentional. */
export interface EmployeeState {
  /** PIN modal visibility — toggled by the hidden activation gestures. */
  modalOpen: boolean;
  /** True once a rep has authenticated. */
  active: boolean;
  token: string | null;
  repName: string | null;
  /** Admin reps can manage promo codes from inside employee mode. */
  isAdmin: boolean;
  /** Promo code: server resolves it to a template + discount. */
  promoId: string;
  /** Chosen quote template id; "" = leave the default. */
  templateId: string;
  /** "" = no manual discount; otherwise percent or fixed-dollar. */
  discountType: "" | DiscountType;
  /** Percent (e.g. 10) when type=percent, dollars when type=fixed. */
  discountValue: number;
  /** Label shown on the quote's discount line (e.g. "Trade-in"). Blank → the
   *  backend defaults it to "Discount". This manual discount is always a global
   *  line; machine-scope is a promo-only concept applied on the line item. */
  discountLabel: string;
}

export interface FinancingParams {
  /** Credit tier the customer self-selects. Maps to APR per CREDIT_APRS. */
  creditTier: CreditTier;
  /** Loan term — Gammill offers 60 / 72 / 84-month conventional terms. */
  termMonths: 60 | 72 | 84;
  /** Cash down at signing. Floors at the $1,000 deposit the configurator
   *  collects via the HubSpot Payment Link in hs_comments. */
  downPaymentCents: number;
}

export type CreditTier = "excellent" | "good" | "fair" | "poor";

/** APR (percent) per credit tier — rates as quoted to Andrew on
 *  2026-05-24 by Gammill's financing partner. Update here when rates
 *  change; the monthly math reads from this map. */
export const CREDIT_APRS: Record<CreditTier, number> = {
  excellent: 10.99,
  good: 13.99,
  fair: 17.99,
  poor: 21.99,
};

export const CREDIT_TIER_LABELS: Record<CreditTier, string> = {
  excellent: "Excellent (720+)",
  good: "Good (660–719)",
  fair: "Fair (600–659)",
  poor: "Building (<600)",
};

const initialCustomer: CustomerInfo = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  address: "",
  city: "",
  state: "",
  zip: "",
};

/** Maps the deep-link `preloved_category` slug to its Deal-Name label.
 *  Unknown / missing → "Pre-Loved". */
const PRELOVED_CATEGORY_LABELS: Record<string, string> = {
  "pre-loved": "Pre-Loved",
  preloved: "Pre-Loved",
  "paint-flaw": "Paint Flaw",
  show: "Show",
  classroom: "Classroom",
};

/** Pre-loved deep-link params, parsed once from the URL at load:
 *  .../?template=preloved&preloved_price_cents=4289900&preloved_description=...
 *      &preloved_family=statler&preloved_product_id=1352372845
 *      &preloved_category=pre-loved&preloved_year=2006  (or &preloved_class=B)
 *      &preloved_throat=26&preloved_image=https%3A%2F%2F...
 *  Null when not a pre-loved launch. */
const PRELOVED: State["preloved"] = (() => {
  try {
    const p = new URLSearchParams(window.location.search);
    if ((p.get("template") || "").toLowerCase() !== "preloved") return null;
    const priceCents = parseInt(p.get("preloved_price_cents") || "", 10);
    if (!Number.isFinite(priceCents) || priceCents <= 0) return null;
    const description =
      (p.get("preloved_description") || "").trim() || "Pre-Loved Gammill Longarm";
    const famRaw = (p.get("preloved_family") || "").toLowerCase();
    const family: MachineFamily =
      famRaw === "vision" || famRaw === "ascend" ? famRaw : "statler";
    const category =
      PRELOVED_CATEGORY_LABELS[
        (p.get("preloved_category") || "").trim().toLowerCase()
      ] ?? "Pre-Loved";
    // Class wins when present (a graded unit has no year); year only when a
    // clean 4-digit value (year ranges are never printed in the deal name).
    const klassRaw = (p.get("preloved_class") || "").trim().toUpperCase();
    const klass = /^[ABC]$/.test(klassRaw) ? klassRaw : null;
    const yearRaw = (p.get("preloved_year") || "").trim();
    const year = !klass && /^\d{4}$/.test(yearRaw) ? yearRaw : null;
    const throatRaw = parseInt(p.get("preloved_throat") || "", 10);
    const throat =
      Number.isFinite(throatRaw) && throatRaw > 0 ? throatRaw : null;
    return {
      productId: p.get("preloved_product_id") || "preloved",
      family,
      description,
      priceCents,
      imageUrl: (p.get("preloved_image") || "").trim(),
      category,
      year,
      klass,
      throat,
    };
  } catch {
    return null;
  }
})();

/** Hidden test trigger: `?promo_preview=<code>` forces that promo active
 *  regardless of its dates, on both the banner and the backend discount, so the
 *  customer-facing experience can be tested outside a promo's window. */
const PROMO_PREVIEW: string = (() => {
  try {
    return (
      new URLSearchParams(window.location.search).get("promo_preview") || ""
    ).trim();
  } catch {
    return "";
  }
})();

/** Request fields that flip the backend into pre-loved pricing + template mode.
 *  Spread into the preview / lead / generate bodies when in pre-loved mode. */
function prelovedReqFields(pl: State["preloved"]) {
  return pl
    ? {
        template: "preloved" as const,
        // `family` + `category` let the backend apply the Statler-only
        // Ascend-upgrade price override ($7,000 Pre-Loved, $6,000 Show /
        // Classroom / Paint Flaw) when re-pricing the quote server-side.
        preloved: {
          description: pl.description,
          price_cents: pl.priceCents,
          family: pl.family,
          category: pl.category,
          // Class A/B/C (graded) vs none (dated) drives the promo sub-type split.
          klass: pl.klass ?? undefined,
        },
      }
    : {};
}

/** Derive the structured Deal-Name pieces from the current selection (or the
 *  pre-loved deep-link). The backend composes the final pipe-delimited string;
 *  this just gathers the parts. Empty/unknown parts are sent as null and the
 *  backend drops them from the name. */
function buildDealMeta(state: State): DealMeta {
  // Pre-loved channel: everything comes from the deep-link.
  if (state.preloved) {
    const pl = state.preloved;
    // A pre-loved Statler with the Ascend upgrade added IS an Ascend — name
    // it "Pre-Loved Ascend …" (the backend keeps "Pre-Loved Ascend"
    // contiguous). Otherwise use the unit's own family.
    const ascendAdded =
      pl.family === "statler" &&
      state.selectedAccessoryIds.includes(ID.ASCEND_UPGRADE);
    return {
      category: pl.category,
      year: pl.year,
      klass: pl.klass,
      machine_type: ascendAdded ? "Ascend" : familyLabel(pl.family),
      machine_size_inches: pl.throat,
      table_size: null, // pre-loved ships with its own table
    };
  }

  const catalog = state.catalog;
  const selected =
    catalog && state.selectedMachineId
      ? catalog.byId.get(state.selectedMachineId)
      : null;

  // Upgrades/retrofits aren't "New" machines: name them by their full product
  // name ("Ascend Retrofit", "Statler Retrofit", "Vision 2 Retrofit") with no
  // category segment and no throat size (the captured size is the customer's
  // CURRENT machine and only muddies the deal name) — e.g.
  // "Jane Smith - Ascend Retrofit".
  const isUpgrade = selected?.category === "upgrade";

  let machineType: string | null = null;
  let sizeInches: number | null = null;
  if (selected?.category === "machine") {
    machineType = familyLabel(selected.family);
    sizeInches = selected.throatSize;
  } else if (isUpgrade) {
    machineType = displayNameFor(selected);
  }

  let tableSize: string | null = null;
  if (catalog && state.selectedTableId) {
    const t = catalog.byId.get(state.selectedTableId);
    if (t?.category === "table") tableSize = `${t.length}' ${t.tableType}`;
  }

  return {
    category: isUpgrade ? "" : "New",
    year: null,
    klass: null,
    machine_type: machineType,
    machine_size_inches: sizeInches,
    table_size: tableSize,
  };
}

// ── Auto-save the in-progress build ──────────────────────────────────
// Persist the configuration to the browser so leaving the tool (via the site
// menu) and coming back restores it. A Pre-Loved deep-link always wins over a
// stale save; the save clears once a quote is generated; entries older than 7
// days are ignored. Only the BUILD is saved — never customer PII or employee
// login.
const BUILD_SAVE_KEY = "gammill-build-v1";
const BUILD_SAVE_TTL_MS = 7 * 24 * 60 * 60 * 1000;

type SavedBuild = Pick<
  State,
  | "selectedMachineId"
  | "selectedAccessoryIds"
  | "selectedTableId"
  | "existingSystemAnswers"
  | "financing"
>;

function loadSavedBuild(): SavedBuild | null {
  if (PRELOVED) return null; // a deep-link build takes precedence
  try {
    const raw = localStorage.getItem(BUILD_SAVE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { ts?: number; data?: SavedBuild };
    if (!parsed.ts || Date.now() - parsed.ts > BUILD_SAVE_TTL_MS) return null;
    return parsed.data ?? null;
  } catch {
    return null;
  }
}

function saveBuild(state: State): void {
  try {
    const data: SavedBuild = {
      selectedMachineId: state.selectedMachineId,
      selectedAccessoryIds: state.selectedAccessoryIds,
      selectedTableId: state.selectedTableId,
      existingSystemAnswers: state.existingSystemAnswers,
      financing: state.financing,
    };
    localStorage.setItem(BUILD_SAVE_KEY, JSON.stringify({ ts: Date.now(), data }));
  } catch {
    /* storage disabled/full — best-effort */
  }
}

function clearSavedBuild(): void {
  try {
    localStorage.removeItem(BUILD_SAVE_KEY);
  } catch {
    /* no-op */
  }
}

const SAVED_BUILD = loadSavedBuild();

const initialState: State = {
  catalog: null,
  catalogLoading: true,
  catalogError: null,
  selectedMachineId: SAVED_BUILD?.selectedMachineId ?? null,
  selectedAccessoryIds: SAVED_BUILD?.selectedAccessoryIds ?? [],
  selectedTableId: SAVED_BUILD?.selectedTableId ?? null,
  existingSystemAnswers: SAVED_BUILD?.existingSystemAnswers ?? {},
  customer: initialCustomer,
  preview: null,
  previewLoading: false,
  previewError: null,
  calculated: false,
  leadCaptured: false,
  capturedContactId: null,
  preloved: PRELOVED,
  activePromos: [],
  generated: null,
  submitting: false,
  submitError: null,
  employee: {
    modalOpen: false,
    active: false,
    token: null,
    repName: null,
    isAdmin: false,
    promoId: "",
    templateId: "",
    discountType: "",
    discountValue: 0,
    discountLabel: "",
  },
  financing: SAVED_BUILD?.financing ?? {
    // Excellent credit assumed for the showcased rate; 60-month term is the
    // default Gammill wants front-and-center (customers can extend to 72/84
    // for a lower monthly via Edit Financing).
    creditTier: "excellent",
    termMonths: 60,
    downPaymentCents: 100_000, // $1,000, mirrors CONFIGURATOR_DEPOSIT_AMOUNT
  },
};

type Action =
  | { type: "catalog/loaded"; products: Product[] }
  | { type: "catalog/failed"; message: string }
  | { type: "promos/loaded"; promos: ActivePromo[] }
  | { type: "machine/select"; id: string }
  | { type: "accessory/toggle"; id: string }
  | { type: "table/select"; id: string }
  | { type: "existingSystem/update"; fieldId: string; value: string }
  | { type: "customer/update"; patch: Partial<CustomerInfo> }
  | { type: "preview/start" }
  | { type: "preview/success"; data: PreviewResponse }
  | { type: "preview/clear" }
  | { type: "preview/failure"; message: string }
  | { type: "calculate/done"; contactId: string | null }
  | { type: "submit/start" }
  | { type: "submit/success"; data: GenerateResponse }
  | { type: "submit/failure"; message: string }
  | { type: "financing/update"; patch: Partial<FinancingParams> }
  | { type: "employee/modal-open" }
  | { type: "employee/modal-close" }
  | { type: "employee/modal-toggle" }
  | { type: "employee/unlock"; token: string; repName: string; isAdmin: boolean }
  | { type: "employee/lock" }
  | {
      type: "employee/set";
      patch: Partial<
        Pick<
          EmployeeState,
          | "promoId"
          | "templateId"
          | "discountType"
          | "discountValue"
          | "discountLabel"
        >
      >;
    }
  | { type: "reset" };

/** Apply the side effects of switching the main selection: default-on
 *  accessories, default table (or null for upgrades), reset existing-system
 *  answers when we leave the upgrade context. */
function applyMachineSelection(state: State, machineId: string): State {
  if (!state.catalog) return { ...state, selectedMachineId: machineId };
  const product = state.catalog.byId.get(machineId);
  if (!product) return state;

  const ctx = {
    catalog: state.catalog,
    selectedMachineId: machineId,
    selectedAccessoryIds: [],
    selectedTableId: null,
    existingSystemAnswers: {},
  };
  const defaultAccessories = defaultAccessoriesFor(ctx);

  let selectedTableId: string | null = null;
  if (product.category === "machine") {
    const defaultTable = tableOptionsForThroatSize(product.throatSize).find(
      (t) => t.isDefault,
    );
    selectedTableId = defaultTable?.id ?? null;
  }

  return {
    ...state,
    selectedMachineId: machineId,
    selectedAccessoryIds: defaultAccessories,
    selectedTableId,
    // Existing-system answers are per-upgrade. Reset when switching
    // to a different upgrade or out of upgrade context.
    existingSystemAnswers:
      product.category === "upgrade" &&
      machineId === state.selectedMachineId
        ? state.existingSystemAnswers
        : {},
  };
}

// Actions that change the configuration itself (what machine, which
// accessories, which table, how the customer answered the existing-system
// questions). After the customer clicks Calculate, any of these has to
// re-gate the totals and revert the button to "Calculate" — that way each
// iteration the customer explores gets recorded as a fresh HubSpot note
// when they re-click. Editing contact-info fields does NOT invalidate
// (fixing a typo in your phone number shouldn't blow away your total).
const CONFIG_MUTATING_ACTIONS: ReadonlySet<Action["type"]> = new Set([
  "machine/select",
  "accessory/toggle",
  "table/select",
  "existingSystem/update",
]);

function reducer(state: State, action: Action): State {
  const next = baseReducer(state, action);
  if (
    state.calculated &&
    next !== state &&
    CONFIG_MUTATING_ACTIONS.has(action.type)
  ) {
    return {
      ...next,
      calculated: false,
      preview: null,
      previewError: null,
      previewLoading: false,
    };
  }
  return next;
}

function baseReducer(state: State, action: Action): State {
  switch (action.type) {
    case "catalog/loaded": {
      const catalog = buildCatalog(action.products);
      // Pre-loved deep-link: inject the fixed used machine as a synthetic
      // catalog entry (Hero / Summary / pricing all read catalog.byId, so the
      // overridden name + price flow for free) and select it. No table or
      // accessories for a used machine.
      if (state.preloved) {
        const pl = state.preloved;
        const synthetic: CategorizedProduct = {
          id: pl.productId,
          name: pl.description,
          description: pl.description,
          price_cents: pl.priceCents,
          sku: "PRELOVED",
          // Per-unit photo when supplied via the deep-link; otherwise empty
          // so Hero falls back to the generic family image.
          image_url: pl.imageUrl,
          category: "machine",
          family: pl.family,
          throatSize: 0,
        };
        catalog.byId.set(pl.productId, synthetic);

        // Pre-loved Statler: surface the Ascend Upgrade as an optional
        // accessory so the buyer can add Ascend the same way a new-Statler
        // buyer gets the "+$6,000 Upgrade to Statler Ascend" toggle. The
        // Machine/Upgrade tab is hidden in pre-loved mode, so reclassifying
        // this one product upgrade→accessory has no effect on the
        // new-machine upgrade flow. Visibility + the price ($7,000 Pre-Loved,
        // $6,000 Show / Classroom / Paint Flaw) live in rules.ts (compat +
        // priceOverride, both gated to Statler family).
        if (pl.family === "statler") {
          const au = catalog.byId.get(ID.ASCEND_UPGRADE);
          if (au && au.category === "upgrade") {
            const asAccessory = { ...au, category: "accessory" } as AccessoryProduct;
            catalog.byId.set(ID.ASCEND_UPGRADE, asAccessory);
            catalog.accessories = [...catalog.accessories, asAccessory];
            catalog.upgrades = catalog.upgrades.filter(
              (u) => u.id !== ID.ASCEND_UPGRADE,
            );
          }
        }
        return {
          ...state,
          catalog,
          catalogLoading: false,
          catalogError: null,
          selectedMachineId: pl.productId,
          selectedTableId: null,
          selectedAccessoryIds: [],
        };
      }
      // Don't auto-select a default machine — the page opens with the
      // splash hero ("Build a Machine") and the customer picks. After
      // anything is selected, normal selection flow kicks in.
      return {
        ...state,
        catalog,
        catalogLoading: false,
        catalogError: null,
        // Honor an explicit prior selection (e.g. resumed session).
        // Otherwise leave selectedMachineId as null so the splash shows.
        selectedMachineId: state.selectedMachineId,
      };
    }
    case "catalog/failed":
      return { ...state, catalogLoading: false, catalogError: action.message };
    case "promos/loaded":
      return { ...state, activePromos: action.promos };
    case "machine/select":
      return applyMachineSelection(state, action.id);
    case "accessory/toggle": {
      const has = state.selectedAccessoryIds.includes(action.id);
      const nextIds = has
        ? state.selectedAccessoryIds.filter((x) => x !== action.id)
        : applyBlocks(
            [...state.selectedAccessoryIds, action.id],
            action.id,
          );
      return { ...state, selectedAccessoryIds: nextIds };
    }
    case "table/select":
      return { ...state, selectedTableId: action.id };
    case "existingSystem/update": {
      const next: State = {
        ...state,
        existingSystemAnswers: {
          ...state.existingSystemAnswers,
          [action.fieldId]: action.value,
        },
      };
      // Prune any selected accessories that have become invisible because
      // of this answer change (e.g. picking metal wheels removes OS Wheel
      // Kit from the selection so the subtotal stays accurate).
      if (!next.catalog) return next;
      const ctx = {
        catalog: next.catalog,
        selectedMachineId: next.selectedMachineId,
        selectedAccessoryIds: next.selectedAccessoryIds,
        selectedTableId: next.selectedTableId,
        existingSystemAnswers: next.existingSystemAnswers,
      };
      const survivingIds = next.selectedAccessoryIds.filter((id) =>
        isAccessoryVisible(id, ctx),
      );
      return survivingIds.length === next.selectedAccessoryIds.length
        ? next
        : { ...next, selectedAccessoryIds: survivingIds };
    }
    case "customer/update":
      return { ...state, customer: { ...state.customer, ...action.patch } };
    case "preview/start":
      return { ...state, previewLoading: true, previewError: null };
    case "preview/success":
      return {
        ...state,
        preview: action.data,
        previewLoading: false,
        previewError: null,
      };
    case "preview/clear":
      return { ...state, preview: null, previewError: null };
    case "preview/failure":
      return { ...state, previewLoading: false, previewError: action.message };
    case "calculate/done":
      return {
        ...state,
        calculated: true,
        leadCaptured: action.contactId !== null,
        capturedContactId: action.contactId,
      };
    case "submit/start":
      return { ...state, submitting: true, submitError: null };
    case "submit/success":
      return {
        ...state,
        generated: action.data,
        submitting: false,
        submitError: null,
      };
    case "submit/failure":
      return { ...state, submitting: false, submitError: action.message };
    case "financing/update": {
      // Floor down payment at $1,000 (the configurator's deposit
      // minimum) ONLY when the patch actually sets it; leave alone
      // otherwise. Building the object explicitly (instead of with a
      // conditional spread) sidesteps any "...false" footgun the
      // minifier might emit oddly.
      const next = { ...state.financing, ...action.patch };
      if (action.patch.downPaymentCents !== undefined) {
        next.downPaymentCents = Math.max(
          100_000,
          action.patch.downPaymentCents,
        );
      }
      return { ...state, financing: next };
    }
    case "employee/modal-open":
      return { ...state, employee: { ...state.employee, modalOpen: true } };
    case "employee/modal-close":
      return { ...state, employee: { ...state.employee, modalOpen: false } };
    case "employee/modal-toggle":
      return {
        ...state,
        employee: { ...state.employee, modalOpen: !state.employee.modalOpen },
      };
    case "employee/unlock":
      return {
        ...state,
        employee: {
          ...state.employee,
          modalOpen: false,
          active: true,
          token: action.token,
          repName: action.repName,
          isAdmin: action.isAdmin,
        },
      };
    case "employee/lock":
      // Full sign-out → back to a pristine (customer) employee block.
      return { ...state, employee: { ...initialState.employee } };
    case "employee/set":
      return { ...state, employee: { ...state.employee, ...action.patch } };
    case "reset": {
      // "Configure another machine" → back to the splash hero state,
      // no pre-selected machine, same as a fresh page load. Catalog is
      // kept so the customer doesn't have to wait for a re-fetch. Employee
      // mode is preserved so a rep stays logged in across customers.
      // initialState carries the page-load saved build — clear the build so
      // the next customer doesn't inherit it (or its trade-in credits).
      return {
        ...initialState,
        selectedMachineId: null,
        selectedAccessoryIds: [],
        selectedTableId: null,
        existingSystemAnswers: {},
        catalog: state.catalog,
        catalogLoading: false,
        employee: state.employee,
      };
    }
    default:
      return state;
  }
}

interface Ctx {
  state: State;
  dispatch: Dispatch<Action>;
  selectedLineItems: { product_id: string; quantity: number }[];
  selectedSubtotalCents: number;
  /** Statler/Ascend Retrofit trade-in credits from the existing-system answers
   *  (pre-tax, off the order). The backend recomputes them for the quote. */
  tradeInCredits: TradeInCredit[];
  tradeInCents: number;
  /** Promo banner state derived from active promos + the current cart. */
  promoBanner: PromoBanner;
  hasShippingAddress: boolean;
  /** Delivery address is complete but outside the contiguous lower-48 — stop
   *  quoting and show the "contact Sales" notice. */
  outsideLower48: boolean;
  /** Required customer fields filled enough to do anything — drives the
   *  Calculate button's enabled state. */
  canCalculate: boolean;
  /** Same as canCalculate plus a machine is selected — drives Email-me-
   *  the-quote button. */
  canEmailQuote: boolean;
  fetchPreview: () => Promise<void>;
  /** Calculate: fires lead capture + preview fetch. Sets calculated=true. */
  calculate: () => Promise<void>;
  /** Out-of-area (non-lower-48): capture the shopper as a Sales lead, no quote. */
  captureSalesLead: () => Promise<string>;
  /** Generate the quote. `sendEmail` controls whether the customer is emailed
   *  (employee "Create Deal & Quote" passes false). Defaults true. */
  submitQuote: (sendEmail?: boolean) => Promise<void>;
  updateFinancing: (patch: Partial<FinancingParams>) => void;
  /** Hidden activation — call on each secret logo tap; opens the employee
   *  PIN modal once enough taps land in quick succession. */
  registerSecretTap: () => void;
  /** Mark employee mode unlocked after a successful PIN/enroll exchange. */
  unlockEmployee: (token: string, repName: string, isAdmin: boolean) => void;
  /** Sign out of employee mode. */
  lockEmployee: () => void;
  /** Update the rep's per-quote selections (promo / template / discount). */
  setEmployee: (
    patch: Partial<
      Pick<
        EmployeeState,
        | "promoId"
        | "templateId"
        | "discountType"
        | "discountValue"
        | "discountLabel"
      >
    >,
  ) => void;
  closeEmployeeModal: () => void;
}

const ConfiguratorContext = createContext<Ctx | null>(null);

export function ConfiguratorProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    let cancelled = false;
    api
      .listProducts()
      .then((products) => {
        if (!cancelled) dispatch({ type: "catalog/loaded", products });
      })
      .catch((err: Error) => {
        if (!cancelled)
          dispatch({ type: "catalog/failed", message: err.message });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Active auto-apply promos, for the banner/nudge. Best-effort: on any failure
  // the banner simply stays hidden. Honors the hidden ?promo_preview trigger.
  useEffect(() => {
    let cancelled = false;
    api
      .promosActive(PROMO_PREVIEW || undefined)
      .then((promos) => {
        if (!cancelled) dispatch({ type: "promos/loaded", promos });
      })
      .catch(() => {
        /* no promos / offline — banner stays hidden */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Auto-save the in-progress build so leaving via the site menu and coming
  // back restores it. Clears once a quote is generated (they're done).
  useEffect(() => {
    if (state.generated) {
      clearSavedBuild();
      return;
    }
    saveBuild(state);
  }, [
    state.generated,
    state.selectedMachineId,
    state.selectedAccessoryIds,
    state.selectedTableId,
    state.existingSystemAnswers,
    state.financing,
  ]);

  // Hidden activation #1 — keyboard chord (Ctrl+Shift+G) toggles the employee
  // PIN modal. Fast on a laptop; ignored on tablets (where the logo-tap
  // gesture below is the way in).
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.ctrlKey && e.shiftKey && (e.key === "g" || e.key === "G")) {
        e.preventDefault();
        dispatch({ type: "employee/modal-toggle" });
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Hidden activation #2 — five quick taps on the header logo opens the modal.
  // Discreet, leaves no URL trace, and works on the iPads reps carry at shows.
  const tapRef = useRef<{ count: number; first: number }>({ count: 0, first: 0 });
  function registerSecretTap() {
    const now = Date.now();
    const t = tapRef.current;
    if (now - t.first > 2000) {
      t.count = 0;
      t.first = now;
    }
    t.count += 1;
    if (t.count >= 5) {
      t.count = 0;
      dispatch({ type: "employee/modal-open" });
    }
  }

  const selectedLineItems = useMemo(() => {
    const ids = [
      state.selectedMachineId,
      state.selectedTableId,
      ...state.selectedAccessoryIds,
    ].filter((x): x is string => Boolean(x));
    return ids.map((id) => ({ product_id: id, quantity: 1 }));
  }, [
    state.selectedMachineId,
    state.selectedTableId,
    state.selectedAccessoryIds,
  ]);

  const selectedSubtotalCents = useMemo(() => {
    if (!state.catalog) return 0;
    const ctx = {
      catalog: state.catalog,
      selectedMachineId: state.selectedMachineId,
      selectedAccessoryIds: state.selectedAccessoryIds,
      selectedTableId: state.selectedTableId,
      existingSystemAnswers: state.existingSystemAnswers,
      prelovedCategory: state.preloved?.category ?? null,
    };
    let total = 0;
    for (const li of selectedLineItems) {
      const p = state.catalog.byId.get(li.product_id);
      if (!p) continue;
      total += effectivePriceCents(li.product_id, p.price_cents, ctx) * li.quantity;
    }
    return total;
  }, [
    state.catalog,
    selectedLineItems,
    state.selectedMachineId,
    state.selectedAccessoryIds,
    state.selectedTableId,
    state.existingSystemAnswers,
  ]);

  const tradeInCredits = useMemo(
    () =>
      state.preloved
        ? []
        : retrofitTradeInCredits(
            state.selectedMachineId,
            state.existingSystemAnswers,
          ),
    [state.preloved, state.selectedMachineId, state.existingSystemAnswers],
  );
  const tradeInCents = tradeInCredits.reduce((sum, c) => sum + c.cents, 0);

  // Main item (machine / retrofit / preloved) classification + price — feeds
  // promo matching and the savings estimate. "Ascend Upgrade" is a distinct
  // kind so it can be excluded from a machines-and-retrofits promo.
  const mainItem = useMemo(() => {
    if (state.preloved) {
      return {
        // Graded (class A/B/C) vs dated (year / none) — matches the promo split.
        kind: state.preloved.klass ? "preloved_graded" : "preloved_dated",
        family: state.preloved.family as string,
        productId: state.preloved.productId,
        priceCents: state.preloved.priceCents,
      };
    }
    const m =
      state.catalog && state.selectedMachineId
        ? state.catalog.byId.get(state.selectedMachineId)
        : null;
    if (!m) return null;
    let kind = "other";
    if (m.category === "machine") kind = "machine";
    else if (m.category === "upgrade")
      kind = m.id === ID.ASCEND_UPGRADE ? "upgrade" : "retrofit";
    const priceCents = effectivePriceCents(m.id, m.price_cents, {
      catalog: state.catalog!,
      selectedMachineId: state.selectedMachineId,
      selectedAccessoryIds: state.selectedAccessoryIds,
      selectedTableId: state.selectedTableId,
      existingSystemAnswers: state.existingSystemAnswers,
    });
    return {
      kind,
      family: (m as { family?: string }).family ?? "",
      productId: m.id,
      priceCents,
    };
  }, [
    state.preloved,
    state.catalog,
    state.selectedMachineId,
    state.selectedAccessoryIds,
    state.selectedTableId,
    state.existingSystemAnswers,
  ]);

  // Banner: a matched promo flips to "applied" (with an estimated savings);
  // otherwise, if any promo is active, "available" nudges the shopper.
  const promoBanner = useMemo<PromoBanner>(() => {
    // Employee mode: a rep's explicit promo pick governs — hide the auto-apply
    // banner so the tool doesn't imply a second promo is being applied.
    if (state.employee.active && state.employee.promoId.trim())
      return { status: null, promo: null, savingsCents: 0 };
    if (!state.activePromos.length)
      return { status: null, promo: null, savingsCents: 0 };
    const matched = mainItem
      ? state.activePromos.find(
          (p) =>
            p.kinds.includes(mainItem.kind) ||
            // A pre-loved sub-kind also matches a legacy generic "preloved" trigger.
            (mainItem.kind.startsWith("preloved_") &&
              p.kinds.includes("preloved")) ||
            (mainItem.family && p.families.includes(mainItem.family)) ||
            p.product_ids.includes(mainItem.productId),
        )
      : undefined;
    if (matched && mainItem) {
      const base =
        matched.discount_scope === "machine"
          ? mainItem.priceCents
          : selectedSubtotalCents;
      const tierOff =
        matched.discount_tiers.find(
          (t) => t.up_to_cents === null || base <= t.up_to_cents,
        )?.off_cents ?? 0;
      const savingsCents =
        matched.discount_type === "percent"
          ? Math.round((base * matched.discount_value) / 100)
          : matched.discount_type === "tiered"
            ? Math.min(tierOff, base)
            : Math.min(Math.round(matched.discount_value * 100), base);
      return { status: "applied", promo: matched, savingsCents };
    }
    return { status: "available", promo: state.activePromos[0], savingsCents: 0 };
  }, [
    state.activePromos,
    mainItem,
    selectedSubtotalCents,
    state.employee.active,
    state.employee.promoId,
  ]);

  // Postal codes: 5 (US), 6 (Canadian "A1A1A1" stripped), or 3-10
  // chars more generally for international. State/Province/Region:
  // at least 2 chars — accept either a 2-letter code ("IL") or a full
  // name ("Illinois"); the backend normalizes both to the 2-letter code
  // HubSpot + TaxJar require. (No upper bound: capping at 3 here used to
  // block customers who typed their state's full name, leaving the
  // address visibly complete but the quote stuck on "Enter your delivery
  // address".)
  const hasShippingAddress = Boolean(
    state.customer.address.trim() &&
      state.customer.city.trim() &&
      state.customer.state.trim().length >= 2 &&
      state.customer.zip.trim().length >= 3 &&
      state.customer.zip.trim().length <= 10,
  );

  const contactComplete =
    hasShippingAddress &&
    state.customer.firstName.trim().length > 0 &&
    state.customer.lastName.trim().length > 0 &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(state.customer.email.trim());

  // Outside the contiguous lower-48: once the address is complete, stop quoting
  // and route to Sales (the backend also enforces this authoritatively).
  const outsideLower48 =
    hasShippingAddress && !isLower48Zip(state.customer.zip);

  const canCalculate =
    !state.submitting &&
    contactComplete &&
    Boolean(state.selectedMachineId) &&
    !outsideLower48;
  const canEmailQuote = canCalculate && state.calculated;

  async function fetchPreview() {
    if (!selectedLineItems.length || !hasShippingAddress || outsideLower48) {
      dispatch({ type: "preview/clear" });
      return;
    }
    dispatch({ type: "preview/start" });
    try {
      const data = await api.previewQuote({
        line_items: selectedLineItems,
        zip: state.customer.zip,
        state: state.customer.state,
        ...(PROMO_PREVIEW ? { promo_preview: PROMO_PREVIEW } : {}),
        ...(state.employee.active && state.employee.promoId.trim()
          ? { employee_promo: true }
          : {}),
        ...prelovedReqFields(state.preloved),
        // Retrofit trade-in credits are re-derived server-side from these.
        existing_system: state.existingSystemAnswers,
      });
      dispatch({ type: "preview/success", data });
    } catch (err) {
      dispatch({
        type: "preview/failure",
        message: err instanceof Error ? err.message : "Preview failed",
      });
    }
  }

  async function calculate() {
    // Fires lead-capture + preview fetch in parallel. Marks calculated=true
    // immediately so the UI flips even if HubSpot is slow — the preview
    // row will show its own loading state until the tax lands.
    const capturePromise = api
      .captureLead({
        customer: {
          first_name: state.customer.firstName,
          last_name: state.customer.lastName,
          email: state.customer.email,
          phone: state.customer.phone,
          address: state.customer.address,
          city: state.customer.city,
          state: state.customer.state,
          zip: state.customer.zip,
        },
        // Backend renders these into a Note on the contact so sales sees
        // exactly what configuration the shopper had on screen.
        line_items: selectedLineItems,
        ...prelovedReqFields(state.preloved),
        // Retrofit trade-in credits are re-derived server-side from these.
        existing_system: state.existingSystemAnswers,
      })
      .then((r) => r.hubspot_contact_id)
      .catch((err) => {
        // Best-effort — log but don't block the customer's UX. The next
        // submit will retry by upserting again.
        console.error("Lead capture failed:", err);
        return null;
      });

    await Promise.all([capturePromise, fetchPreview()]).then(([contactId]) => {
      dispatch({ type: "calculate/done", contactId });
    });
    // High-intent page-signal to Emily: a real shopper (not a rep building a
    // quote) configured a machine and saw a price — score this above a bounce.
    if (!state.employee.active) signalPriceCalculated();
  }

  // Out-of-area (non-lower-48): capture the shopper as a lead — no quote. The
  // backend flags the note so sales follows up with shipping + pricing.
  async function captureSalesLead() {
    return api
      .captureLead({
        customer: {
          first_name: state.customer.firstName,
          last_name: state.customer.lastName,
          email: state.customer.email,
          phone: state.customer.phone,
          address: state.customer.address,
          city: state.customer.city,
          state: state.customer.state,
          zip: state.customer.zip,
        },
        line_items: selectedLineItems,
        ...prelovedReqFields(state.preloved),
        // Retrofit trade-in credits are re-derived server-side from these.
        existing_system: state.existingSystemAnswers,
      })
      .then((r) => r.hubspot_contact_id);
  }

  async function submitQuote(sendEmail = true) {
    dispatch({ type: "submit/start" });
    try {
      const data = await api.generateQuote({
        line_items: selectedLineItems,
        // Employee "Create Deal & Quote" passes false to skip the customer
        // email (the HubSpot workflow that watches the trigger property).
        send_email: sendEmail,
        // Structured pieces for the composed HubSpot Deal Name.
        deal_meta: buildDealMeta(state),
        customer: {
          first_name: state.customer.firstName,
          last_name: state.customer.lastName,
          email: state.customer.email,
          phone: state.customer.phone,
          address: state.customer.address,
          city: state.customer.city,
          state: state.customer.state,
          zip: state.customer.zip,
        },
        // Carry the customer's selected credit tier + term + down to
        // the backend so the HubSpot quote's monthly-payment display
        // matches what they saw on the configurator (not the old
        // hardcoded Excellent 10.99% / 5+7yr default).
        financing: {
          credit_tier: state.financing.creditTier,
          term_months: state.financing.termMonths,
          down_payment_cents: state.financing.downPaymentCents,
        },
        ...(PROMO_PREVIEW ? { promo_preview: PROMO_PREVIEW } : {}),
        ...prelovedReqFields(state.preloved),
        // Retrofit trade-in credits are re-derived server-side from these.
        existing_system: state.existingSystemAnswers,
        // Employee superpowers — only when a rep is authenticated. The backend
        // re-validates the token and ignores all of this otherwise.
        ...(state.employee.active && state.employee.token
          ? {
              employee: {
                token: state.employee.token,
                promo_id: state.employee.promoId.trim() || undefined,
                template_id: state.employee.templateId || undefined,
                discount: state.employee.discountType
                  ? {
                      type: state.employee.discountType,
                      value: state.employee.discountValue,
                      label: state.employee.discountLabel.trim() || undefined,
                    }
                  : undefined,
              },
            }
          : {}),
      });
      dispatch({ type: "submit/success", data });
      // Hottest page-signal to Emily: a real shopper generated a quote. Skipped
      // in employee mode (a rep building the quote isn't the buyer).
      if (!state.employee.active) signalQuoteRequested();
    } catch (err) {
      dispatch({
        type: "submit/failure",
        message:
          err instanceof Error
            ? err.message
            : "Could not generate quote — please try again or call us.",
      });
    }
  }

  const ctx: Ctx = {
    state,
    dispatch,
    selectedLineItems,
    selectedSubtotalCents,
    tradeInCredits,
    tradeInCents,
    promoBanner,
    hasShippingAddress,
    outsideLower48,
    canCalculate,
    canEmailQuote,
    fetchPreview,
    calculate,
    captureSalesLead,
    submitQuote,
    updateFinancing: (patch) => dispatch({ type: "financing/update", patch }),
    registerSecretTap,
    unlockEmployee: (token, repName, isAdmin) =>
      dispatch({ type: "employee/unlock", token, repName, isAdmin }),
    lockEmployee: () => dispatch({ type: "employee/lock" }),
    setEmployee: (patch) => dispatch({ type: "employee/set", patch }),
    closeEmployeeModal: () => dispatch({ type: "employee/modal-close" }),
  };

  return (
    <ConfiguratorContext.Provider value={ctx}>
      {children}
    </ConfiguratorContext.Provider>
  );
}

export function useConfigurator(): Ctx {
  const ctx = useContext(ConfiguratorContext);
  if (!ctx) throw new Error("useConfigurator must be inside ConfiguratorProvider");
  return ctx;
}
